import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { projectToast } from "@project/lib/toast";
import { getApiErrorMessage } from "@project/lib/api-error";
import { useI18n } from "@project/lib/use-i18n";
import {
  FormStepper,
  type FormStep,
} from "@project/user/components/form-stepper";
import { Input } from "@project/components/ui/input";
import { Textarea } from "@project/components/ui/textarea";
import { Label } from "@project/components/ui/label";
import { Button } from "@project/components/ui/button";
import { Switch } from "@project/components/ui/switch";
import { Checkbox } from "@project/components/ui/checkbox";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "@project/admin/components/searchable-select";
import { relationshipService } from "@project/services/admin/relationship.service";
import { treatmentMasterService } from "@project/services/admin/treatment-master.service";
import { admissionInquiryService } from "@project/services/user/admission-inquiry.service";
import type { AdmissionInquiryRecord } from "@project/types/admission-inquiry";
import {
  INITIAL_INQUIRY_FORM,
  buildInquiryPayload,
  createInquiryFamilyRow,
  createInquiryFamilyRows,
  createMedicineRow,
  createMedicineRows,
  createOperationRow,
  createOperationRows,
  inquiryRecordToFamilyMembers,
  inquiryRecordToFormState,
  inquiryRecordToMedicines,
  inquiryRecordToOperations,
  validateInquiryForm,
  validateInquiryStep,
  type AdmissionInquiryFormState,
  type InquiryFamilyMember,
  type InquiryMedicine,
  type InquiryOperation,
} from "@project/components/admission-inquiry-form.utils";
import {
  EMPTY_LOCATION_LABELS,
  locationSeedOption,
  resolveLocationLabels,
  searchAreaOptions,
  searchCityOptions,
  searchDistrictOptions,
  useLocationCascade,
  type LocationLabels,
} from "@project/components/location-cascading-selects";
import {
  ClipboardList,
  Heart,
  Wallet,
  Activity,
  Stethoscope,
  Scissors,
  Users2,
  FileText,
  Plus,
  Trash2,
  Send,
  Save,
  ChevronLeft,
  ChevronRight,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@project/lib/utils";
import { AdmissionInquiryConfirmDialog } from "@project/user/components/admission-inquiry-confirm-dialog";
import { MobileNumberField } from "@project/components/mobile-number-field";
import type { PhoneCountryCode } from "@project/lib/mobile-number";

type StepKey =
  | "inquiry"
  | "family"
  | "elder"
  | "income"
  | "health"
  | "treatment"
  | "operation"
  | "notes";

type StepConfig = FormStep & {
  icon: LucideIcon;
  eyebrow: string;
  subtitle: string;
};

export type { AdmissionInquiryFormState } from "@project/components/admission-inquiry-form.utils";

const STEP_DEFS: { key: StepKey; icon: LucideIcon }[] = [
  { key: "inquiry", icon: ClipboardList },
  { key: "family", icon: Users2 },
  { key: "elder", icon: Heart },
  { key: "income", icon: Wallet },
  { key: "health", icon: Activity },
  { key: "treatment", icon: Stethoscope },
  { key: "operation", icon: Scissors },
  { key: "notes", icon: FileText },
];

// Sentinel value used by the treatment-name dropdown to let the user switch to
// manual text entry when the treatment isn't in the master list.
const TREATMENT_OTHER_VALUE = "__other__";
const RELATIONSHIP_OTHER_VALUE = "__other__";

function StepPanel({
  step,
  children,
}: {
  step: StepConfig;
  children: ReactNode;
}) {
  const Icon = step.icon;
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-6">
      <div className="mb-5 flex items-start gap-3 border-b border-border pb-4">
        <div className="bg-primary/10 grid h-11 w-11 shrink-0 place-items-center rounded-xl text-primary">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-primary">
            {step.eyebrow}
          </div>
          <h2 className="font-serif text-lg font-bold text-foreground sm:text-xl">
            {step.label}
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground sm:text-sm">
            {step.subtitle}
          </p>
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2">{children}</div>
    </div>
  );
}

function Field({
  label,
  children,
  full = false,
  required = false,
}: {
  label: string;
  children: ReactNode;
  full?: boolean;
  required?: boolean;
}) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-foreground/80 mb-1.5 block text-xs font-medium">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </Label>
      {children}
    </div>
  );
}

function ToggleField({
  label,
  description,
  checked,
  onCheckedChange,
  className,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onCheckedChange: (value: boolean) => void;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 rounded-xl border border-border bg-background px-4 py-3 md:col-span-2",
        className
      )}
    >
      <div>
        <p className="text-foreground/90 text-sm font-medium">{label}</p>
        {description && (
          <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
        )}
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  );
}

function patchFormField<K extends keyof AdmissionInquiryFormState>(
  key: K,
  value: AdmissionInquiryFormState[K]
) {
  return (prev: AdmissionInquiryFormState) =>
    prev[key] === value ? prev : { ...prev, [key]: value };
}

export type AdmissionInquiryFormProps = {
  formId?: string;
  mode?: "user" | "admin";
  className?: string;
  editingId?: number;
  initialRecord?: AdmissionInquiryRecord;
  onSubmitted?: (summary: { inquirerName: string; elderName: string }) => void;
};

export function AdmissionInquiryForm({
  formId = "admission-inquiry-form",
  mode = "user",
  className,
  editingId,
  initialRecord,
  onSubmitted,
}: AdmissionInquiryFormProps) {
  const { t } = useI18n();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [form, setForm] =
    useState<AdmissionInquiryFormState>(INITIAL_INQUIRY_FORM);
  const [medicines, setMedicines] = useState<InquiryMedicine[]>(() =>
    createMedicineRows(1)
  );
  const [operations, setOperations] = useState<InquiryOperation[]>(() =>
    createOperationRows(1)
  );
  const [familyMembers, setFamilyMembers] = useState<InquiryFamilyMember[]>(
    () => createInquiryFamilyRows(3)
  );
  const location = useLocationCascade();
  const elderLocation = useLocationCascade();
  const setLocationValue = location.setValue;
  const setElderLocationValue = elderLocation.setValue;
  const [inquirerLocationLabels, setInquirerLocationLabels] =
    useState<LocationLabels>(EMPTY_LOCATION_LABELS);
  const [elderLocationLabels, setElderLocationLabels] =
    useState<LocationLabels>(EMPTY_LOCATION_LABELS);
  const inquirerLocationLabelsRef = useRef(inquirerLocationLabels);
  inquirerLocationLabelsRef.current = inquirerLocationLabels;
  const elderLocationLabelsRef = useRef(elderLocationLabels);
  elderLocationLabelsRef.current = elderLocationLabels;
  const [sameAsInquirerAddress, setSameAsInquirerAddress] = useState(false);

  const copyInquirerAddressToElder = useCallback(async () => {
    const ids = location.value;
    const labels = await resolveLocationLabels(
      ids,
      inquirerLocationLabelsRef.current
    );

    setInquirerLocationLabels(prev => {
      if (
        prev.districtName === labels.districtName &&
        prev.cityName === labels.cityName &&
        prev.areaName === labels.areaName
      ) {
        return prev;
      }
      return labels;
    });

    setElderLocationValue(prev => {
      if (
        prev.districtId === ids.districtId &&
        prev.cityId === ids.cityId &&
        prev.areaId === ids.areaId
      ) {
        return prev;
      }
      return {
        districtId: ids.districtId,
        cityId: ids.cityId,
        areaId: ids.areaId,
      };
    });

    setElderLocationLabels(prev => {
      if (
        prev.districtName === labels.districtName &&
        prev.cityName === labels.cityName &&
        prev.areaName === labels.areaName
      ) {
        return prev;
      }
      return labels;
    });

    setForm(prev => {
      if (
        prev.elderPincode === prev.pincode &&
        prev.elderLandmark === prev.landmark
      ) {
        return prev;
      }
      return {
        ...prev,
        elderPincode: prev.pincode,
        elderLandmark: prev.landmark,
      };
    });
  }, [location.value, setElderLocationValue]);

  useEffect(() => {
    if (!sameAsInquirerAddress) {
      return;
    }
    void copyInquirerAddressToElder();
  }, [
    sameAsInquirerAddress,
    location.value.districtId,
    location.value.cityId,
    location.value.areaId,
    form.pincode,
    form.landmark,
    copyInquirerAddressToElder,
  ]);

  useEffect(() => {
    if (step !== 0 && step !== 2) {
      return;
    }

    let cancelled = false;
    const ids = {
      districtId: location.value.districtId,
      cityId: location.value.cityId,
      areaId: location.value.areaId,
    };
    void resolveLocationLabels(ids, inquirerLocationLabelsRef.current).then(
      labels => {
        if (cancelled) {
          return;
        }
        setInquirerLocationLabels(prev => {
          if (
            prev.districtName === labels.districtName &&
            prev.cityName === labels.cityName &&
            prev.areaName === labels.areaName
          ) {
            return prev;
          }
          return labels;
        });
      }
    );

    return () => {
      cancelled = true;
    };
  }, [
    step,
    location.value.districtId,
    location.value.cityId,
    location.value.areaId,
  ]);

  useEffect(() => {
    if (step !== 2 || sameAsInquirerAddress) {
      return;
    }

    let cancelled = false;
    const ids = {
      districtId: elderLocation.value.districtId,
      cityId: elderLocation.value.cityId,
      areaId: elderLocation.value.areaId,
    };
    void resolveLocationLabels(ids, elderLocationLabelsRef.current).then(
      labels => {
        if (cancelled) {
          return;
        }
        setElderLocationLabels(prev => {
          if (
            prev.districtName === labels.districtName &&
            prev.cityName === labels.cityName &&
            prev.areaName === labels.areaName
          ) {
            return prev;
          }
          return labels;
        });
      }
    );

    return () => {
      cancelled = true;
    };
  }, [
    step,
    sameAsInquirerAddress,
    elderLocation.value.districtId,
    elderLocation.value.cityId,
    elderLocation.value.areaId,
  ]);

  const operationLocationKey = useMemo(
    () =>
      operations
        .map(
          row =>
            `${row.rowId}:${row.districtId}:${row.cityId}:${row.areaId}:${row.districtName}:${row.cityName}:${row.areaName}`
        )
        .join("|"),
    [operations]
  );
  const operationsRef = useRef(operations);
  operationsRef.current = operations;

  useEffect(() => {
    if (step !== 6) {
      return;
    }

    let cancelled = false;
    const rowsSnapshot = operationsRef.current;
    void Promise.all(
      rowsSnapshot.map(async row => {
        const labels = await resolveLocationLabels(
          {
            districtId: row.districtId,
            cityId: row.cityId,
            areaId: row.areaId,
          },
          {
            districtName: row.districtName,
            cityName: row.cityName,
            areaName: row.areaName,
          }
        );
        return { rowId: row.rowId, labels };
      })
    ).then(results => {
      if (cancelled) {
        return;
      }
      setOperations(rows => {
        let changed = false;
        const next = rows.map(row => {
          const resolved = results.find(item => item.rowId === row.rowId);
          if (!resolved) {
            return row;
          }
          if (
            resolved.labels.districtName === row.districtName &&
            resolved.labels.cityName === row.cityName &&
            resolved.labels.areaName === row.areaName
          ) {
            return row;
          }
          changed = true;
          return {
            ...row,
            districtName: resolved.labels.districtName,
            cityName: resolved.labels.cityName,
            areaName: resolved.labels.areaName,
          };
        });
        return changed ? next : rows;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [step, operationLocationKey]);

  const handleSameAsInquirerAddress = (checked: boolean) => {
    setSameAsInquirerAddress(checked);
    if (checked) {
      void copyInquirerAddressToElder();
    }
  };

  const elderAddressLabels = useMemo(
    () =>
      sameAsInquirerAddress ? inquirerLocationLabels : elderLocationLabels,
    [sameAsInquirerAddress, inquirerLocationLabels, elderLocationLabels]
  );

  const setInquirerDistrict = (
    value: string,
    option?: SearchableSelectOption
  ) => {
    location.setDistrictId(value);
    setInquirerLocationLabels({
      districtName: option?.label ?? "",
      cityName: "",
      areaName: "",
    });
  };

  const setInquirerCity = (value: string, option?: SearchableSelectOption) => {
    location.setCityId(value);
    setInquirerLocationLabels(prev => ({
      ...prev,
      cityName: option?.label ?? "",
      areaName: "",
    }));
  };

  const setInquirerArea = (value: string, option?: SearchableSelectOption) => {
    location.setAreaId(value);
    setInquirerLocationLabels(prev => ({
      ...prev,
      areaName: option?.label ?? "",
    }));
  };

  const setElderDistrict = (value: string, option?: SearchableSelectOption) => {
    setSameAsInquirerAddress(false);
    elderLocation.setDistrictId(value);
    setElderLocationLabels({
      districtName: option?.label ?? "",
      cityName: "",
      areaName: "",
    });
  };

  const setElderCity = (value: string, option?: SearchableSelectOption) => {
    setSameAsInquirerAddress(false);
    elderLocation.setCityId(value);
    setElderLocationLabels(prev => ({
      ...prev,
      cityName: option?.label ?? "",
      areaName: "",
    }));
  };

  const setElderArea = (value: string, option?: SearchableSelectOption) => {
    setSameAsInquirerAddress(false);
    elderLocation.setAreaId(value);
    setElderLocationLabels(prev => ({
      ...prev,
      areaName: option?.label ?? "",
    }));
  };

  const steps: StepConfig[] = STEP_DEFS.map(def => ({
    icon: def.icon,
    label: t(`account.inquiryForm.steps.${def.key}.label`),
    shortLabel: t(`account.inquiryForm.steps.${def.key}.shortLabel`),
    eyebrow: t(`account.inquiryForm.steps.${def.key}.eyebrow`),
    subtitle: t(`account.inquiryForm.steps.${def.key}.subtitle`),
  }));
  const current = steps[step];
  const isFirst = step === 0;
  const isLast = step === steps.length - 1;
  const isAdmin = mode === "admin";
  const isEditing = editingId !== undefined;

  useEffect(() => {
    if (!initialRecord) {
      return;
    }
    setForm(inquiryRecordToFormState(initialRecord));
    setMedicines(inquiryRecordToMedicines(initialRecord));
    setOperations(inquiryRecordToOperations(initialRecord));
    setFamilyMembers(inquiryRecordToFamilyMembers(initialRecord));
    setLocationValue({
      districtId: String(initialRecord.district ?? ""),
      cityId: String(initialRecord.city ?? ""),
      areaId: String(initialRecord.area ?? ""),
    });
    setElderLocationValue({
      districtId: String(initialRecord.elder_district ?? ""),
      cityId: String(initialRecord.elder_city ?? ""),
      areaId: String(initialRecord.elder_area ?? ""),
    });
    setInquirerLocationLabels({
      districtName: initialRecord.district_name ?? "",
      cityName: initialRecord.city_name ?? "",
      areaName: initialRecord.area_name ?? "",
    });
    setElderLocationLabels({
      districtName: initialRecord.elder_district_name ?? "",
      cityName: initialRecord.elder_city_name ?? "",
      areaName: initialRecord.elder_area_name ?? "",
    });
    setStep(0);
  }, [initialRecord, editingId, setLocationValue, setElderLocationValue]);

  const setField = <K extends keyof AdmissionInquiryFormState>(
    key: K,
    value: AdmissionInquiryFormState[K]
  ) => {
    setForm(patchFormField(key, value));
  };

  const searchRelationships = useCallback(
    async (query: string) => {
      const result = await relationshipService.listRelationshipsPage({
        page: 1,
        page_size: 50,
        search: query || undefined,
      });
      // Store relationship name (not id) so the payload sends the name string.
      const options = result.data
        .filter(r => r.is_active)
        .map(r => ({
          value: r.relationship_name,
          label: r.relationship_name,
        }));
      return [
        ...options,
        {
          value: RELATIONSHIP_OTHER_VALUE,
          label: t(
            "account.inquiryForm.fields.familyMembers.relationshipOther"
          ),
        },
      ];
    },
    [t]
  );

  const searchTreatments = useCallback(
    async (query: string) => {
      const result = await treatmentMasterService.listTreatmentMasterPage({
        page: 1,
        page_size: 50,
        search: query || undefined,
      });
      // We store the treatment name (not id) so the payload sends the name.
      const options = result.data
        .filter(item => item.is_active)
        .map(item => ({ value: item.name, label: item.name }));
      return [
        ...options,
        {
          value: TREATMENT_OTHER_VALUE,
          label: t("account.inquiryForm.fields.medicines.treatmentOther"),
        },
      ];
    },
    [t]
  );

  const updateMedicine = (rowId: string, patch: Partial<InquiryMedicine>) => {
    setMedicines(rows =>
      rows.map(row => (row.rowId === rowId ? { ...row, ...patch } : row))
    );
  };

  const updateOperation = (rowId: string, patch: Partial<InquiryOperation>) => {
    setOperations(rows =>
      rows.map(row => (row.rowId === rowId ? { ...row, ...patch } : row))
    );
  };

  const updateFamilyMember = (
    rowId: string,
    patch: Partial<InquiryFamilyMember>
  ) => {
    setFamilyMembers(rows =>
      rows.map(row => (row.rowId === rowId ? { ...row, ...patch } : row))
    );
  };

  const resetForm = () => {
    setForm(INITIAL_INQUIRY_FORM);
    setMedicines(createMedicineRows(1));
    setOperations(createOperationRows(1));
    setFamilyMembers(createInquiryFamilyRows(3));
    setLocationValue({ districtId: "", cityId: "", areaId: "" });
    setElderLocationValue({ districtId: "", cityId: "", areaId: "" });
    setInquirerLocationLabels(EMPTY_LOCATION_LABELS);
    setElderLocationLabels(EMPTY_LOCATION_LABELS);
    setSameAsInquirerAddress(false);
    setStep(0);
  };

  const goNext = () => {
    if (isLast) {
      return;
    }
    const stepError = validateInquiryStep(
      step,
      form,
      location.value,
      elderLocation.value,
      familyMembers
    );
    if (stepError) {
      projectToast.error(t(stepError.messageKey, stepError.params));
      return;
    }
    setStep(s => s + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goBack = () => {
    if (isFirst) {
      return;
    }
    setStep(s => s - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async () => {
    if (submitting) {
      return;
    }

    const validationError = validateInquiryForm(
      form,
      location.value,
      elderLocation.value,
      familyMembers
    );
    if (validationError) {
      projectToast.error(t(validationError.messageKey, validationError.params));
      setStep(validationError.step);
      setConfirmOpen(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setSubmitting(true);
    try {
      const payload = buildInquiryPayload(
        form,
        location.value,
        elderLocation.value,
        medicines,
        operations,
        familyMembers
      );
      const summary = {
        inquirerName: form.inquirerName.trim(),
        elderName: form.elderName.trim(),
      };
      if (isEditing) {
        await admissionInquiryService.update(editingId, payload);
        if (isAdmin) {
          projectToast.success(t("account.inquiryForm.toast.updated"));
        }
      } else {
        await admissionInquiryService.create(payload);
        if (isAdmin) {
          projectToast.success(t("account.inquiryForm.toast.saved"));
        }
        resetForm();
      }
      setConfirmOpen(false);
      onSubmitted?.(summary);
    } catch (error) {
      projectToast.error(
        getApiErrorMessage(
          error,
          isEditing
            ? t("account.inquiryForm.toast.updateFailed")
            : t("account.inquiryForm.toast.submitFailed")
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  const requestSubmit = () => {
    if (submitting) {
      return;
    }

    const validationError = validateInquiryForm(
      form,
      location.value,
      elderLocation.value,
      familyMembers
    );
    if (validationError) {
      projectToast.error(t(validationError.messageKey, validationError.params));
      setStep(validationError.step);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    if (!isAdmin && !isEditing) {
      setConfirmOpen(true);
      return;
    }

    void handleSubmit();
  };

  const handleConfirmOpenChange = (open: boolean) => {
    if (submitting) {
      return;
    }
    setConfirmOpen(open);
  };

  const handleFormKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key !== "Enter") {
      return;
    }
    const target = e.target as HTMLElement;
    if (target.tagName === "TEXTAREA") {
      return;
    }
    e.preventDefault();
    if (isLast) {
      requestSubmit();
    } else {
      goNext();
    }
  };

  const fp = (k: string) => t(`account.inquiryForm.fields.${k}`);

  return (
    <div className={cn("min-w-0", className)}>
      {!isAdmin && (
        <AdmissionInquiryConfirmDialog
          open={confirmOpen}
          onOpenChange={handleConfirmOpenChange}
          inquirerName={form.inquirerName.trim()}
          elderName={form.elderName.trim()}
          applicationDate={form.applicationDate}
          submitting={submitting}
          onConfirm={handleSubmit}
        />
      )}
      <FormStepper steps={steps} currentStep={step} className="mb-8" />

      <form
        id={formId}
        onSubmit={e => {
          e.preventDefault();
          requestSubmit();
        }}
        onKeyDown={handleFormKeyDown}
        className="space-y-6"
      >
        {/* Step 1 — Inquiry Details */}
        {step === 0 && (
          <StepPanel step={current}>
            <Field label={fp("applicationDate.label")} required>
              <Input
                type="date"
                value={form.applicationDate}
                onChange={e => setField("applicationDate", e.target.value)}
              />
            </Field>
            <Field label={fp("inquirerName.label")} required>
              <Input
                value={form.inquirerName}
                onChange={e => setField("inquirerName", e.target.value)}
                placeholder={fp("inquirerName.placeholder")}
              />
            </Field>
            <Field label={fp("inquirerMobile.label")} required>
              <MobileNumberField
                value={form.inquirerMobile}
                country={form.inquirerMobileCountry}
                onCountryChange={code =>
                  setField("inquirerMobileCountry", code)
                }
                onChange={value => setField("inquirerMobile", value)}
                placeholder={fp("inquirerMobile.placeholder")}
              />
            </Field>
            <Field label={fp("inquirerEmail.label")} required>
              <Input
                type="email"
                value={form.inquirerEmail}
                onChange={e => setField("inquirerEmail", e.target.value)}
                placeholder={fp("inquirerEmail.placeholder")}
              />
            </Field>
            <Field label={fp("district.label")} required>
              <SearchableSelect
                value={location.value.districtId}
                onValueChange={setInquirerDistrict}
                onSearch={location.searchDistricts}
                seedOption={locationSeedOption(
                  location.value.districtId,
                  inquirerLocationLabels.districtName
                )}
                placeholder={fp("district.placeholder")}
                searchPlaceholder={fp("district.searchPlaceholder")}
                emptyText={fp("district.emptyText")}
              />
            </Field>
            <Field label={fp("city.label")} required>
              <SearchableSelect
                value={location.value.cityId}
                onValueChange={setInquirerCity}
                onSearch={location.searchCities}
                seedOption={locationSeedOption(
                  location.value.cityId,
                  inquirerLocationLabels.cityName
                )}
                placeholder={location.cityPlaceholder}
                searchPlaceholder={fp("city.searchPlaceholder")}
                emptyText={
                  location.cityDisabled
                    ? fp("city.emptyDisabled")
                    : fp("city.empty")
                }
                disabled={location.cityDisabled}
              />
            </Field>
            <Field label={fp("area.label")} required>
              <SearchableSelect
                value={location.value.areaId}
                onValueChange={setInquirerArea}
                onSearch={location.searchAreas}
                seedOption={locationSeedOption(
                  location.value.areaId,
                  inquirerLocationLabels.areaName
                )}
                placeholder={location.areaPlaceholder}
                searchPlaceholder={fp("area.searchPlaceholder")}
                emptyText={
                  location.areaDisabled
                    ? fp("area.emptyDisabled")
                    : fp("area.empty")
                }
                disabled={location.areaDisabled}
              />
            </Field>
            <Field label={fp("pincode.label")} required>
              <Input
                value={form.pincode}
                onChange={e =>
                  setField("pincode", e.target.value.replace(/[^0-9]/g, ""))
                }
                placeholder={fp("pincode.placeholder")}
                maxLength={6}
                inputMode="numeric"
              />
            </Field>
            <Field label={fp("landmark.label")} full>
              <Input
                value={form.landmark}
                onChange={e => setField("landmark", e.target.value)}
                placeholder={fp("landmark.placeholder")}
              />
            </Field>
            <Field label={fp("referencePersonName.label")}>
              <Input
                value={form.referencePersonName}
                onChange={e => setField("referencePersonName", e.target.value)}
                placeholder={fp("referencePersonName.placeholder")}
              />
            </Field>
            <Field label={fp("referencePersonMobile.label")}>
              <MobileNumberField
                value={form.referencePersonMobile}
                country={form.referencePersonMobileCountry}
                onCountryChange={code =>
                  setField("referencePersonMobileCountry", code)
                }
                onChange={value => setField("referencePersonMobile", value)}
                placeholder={fp("referencePersonMobile.placeholder")}
              />
            </Field>
          </StepPanel>
        )}

        {/* Step 2 — Family Members */}
        {step === 1 && (
          <StepPanel step={current}>
            <div className="md:col-span-2">
              <div className="mb-4 flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-foreground">
                  {fp("familyMembers.label")}
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="rounded-xl"
                  onClick={() =>
                    setFamilyMembers(rows => [
                      ...rows,
                      createInquiryFamilyRow(),
                    ])
                  }
                >
                  <Plus className="mr-1 h-3.5 w-3.5" />{" "}
                  {fp("familyMembers.addMember")}
                </Button>
              </div>

              <div className="space-y-3">
                {familyMembers.map((row, i) => (
                  <div
                    key={row.rowId}
                    className="rounded-xl border border-border bg-background p-4"
                  >
                    <div className="mb-3 flex items-center justify-between border-b border-border pb-2">
                      <span className="text-xs font-semibold text-muted-foreground">
                        {fp("familyMembers.member")} {i + 1}
                      </span>
                      {familyMembers.length > 3 && (
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-destructive"
                          onClick={() =>
                            setFamilyMembers(rows =>
                              rows.filter(r => r.rowId !== row.rowId)
                            )
                          }
                          aria-label={`${fp("familyMembers.remove")} ${i + 1}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <div className="grid gap-3 md:grid-cols-2">
                      <Field label={fp("familyMembers.name")} required={i < 3}>
                        <Input
                          value={row.name}
                          onChange={e =>
                            updateFamilyMember(row.rowId, {
                              name: e.target.value,
                            })
                          }
                          placeholder={fp("familyMembers.namePlaceholder")}
                        />
                      </Field>
                      <Field
                        label={fp("familyMembers.relationship")}
                        required={i < 3}
                      >
                        {row.relationshipCustom ? (
                          <div className="space-y-1.5">
                            <Input
                              value={row.relationship}
                              onChange={e =>
                                updateFamilyMember(row.rowId, {
                                  relationship: e.target.value,
                                })
                              }
                              placeholder={fp(
                                "familyMembers.relationshipCustomPlaceholder"
                              )}
                            />
                            <button
                              type="button"
                              className="text-xs font-medium text-primary hover:underline"
                              onClick={() =>
                                updateFamilyMember(row.rowId, {
                                  relationshipCustom: false,
                                  relationship: "",
                                })
                              }
                            >
                              {fp("familyMembers.relationshipBackToList")}
                            </button>
                          </div>
                        ) : (
                          <SearchableSelect
                            value={row.relationship}
                            onValueChange={v => {
                              if (v === RELATIONSHIP_OTHER_VALUE) {
                                updateFamilyMember(row.rowId, {
                                  relationshipCustom: true,
                                  relationship: "",
                                });
                                return;
                              }
                              updateFamilyMember(row.rowId, {
                                relationship: v,
                                relationshipCustom: false,
                              });
                            }}
                            onSearch={searchRelationships}
                            seedOption={
                              row.relationship
                                ? {
                                    value: row.relationship,
                                    label: row.relationship,
                                  }
                                : undefined
                            }
                            placeholder={fp(
                              "familyMembers.relationshipPlaceholder"
                            )}
                            searchPlaceholder={fp(
                              "familyMembers.relationshipSearch"
                            )}
                            emptyText={fp("familyMembers.relationshipEmpty")}
                          />
                        )}
                      </Field>
                      <Field
                        label={fp("familyMembers.mobile")}
                        required={i < 3}
                      >
                        <MobileNumberField
                          value={row.mobile}
                          country={row.mobileCountry}
                          onCountryChange={(code: PhoneCountryCode) =>
                            updateFamilyMember(row.rowId, {
                              mobileCountry: code,
                            })
                          }
                          onChange={value =>
                            updateFamilyMember(row.rowId, { mobile: value })
                          }
                          placeholder={fp("familyMembers.mobilePlaceholder")}
                        />
                      </Field>
                      <Field
                        label={fp("familyMembers.details")}
                        required={i < 3}
                      >
                        <Input
                          value={row.details}
                          onChange={e =>
                            updateFamilyMember(row.rowId, {
                              details: e.target.value,
                            })
                          }
                          placeholder={fp("familyMembers.detailsPlaceholder")}
                        />
                      </Field>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </StepPanel>
        )}

        {/* Step 3 — Elder Details */}
        {step === 2 && (
          <StepPanel step={current}>
            <Field label={fp("elderName.label")} required>
              <Input
                value={form.elderName}
                onChange={e => setField("elderName", e.target.value)}
                placeholder={fp("elderName.placeholder")}
              />
            </Field>
            <div>
              <span
                className="mb-1.5 hidden h-[18px] md:block"
                aria-hidden="true"
              />
              <label className="flex h-9 w-full cursor-pointer items-center gap-2.5 rounded-md border border-input bg-background px-3 shadow-sm">
                <Checkbox
                  checked={sameAsInquirerAddress}
                  onCheckedChange={value =>
                    handleSameAsInquirerAddress(value === true)
                  }
                />
                <span className="text-sm font-medium leading-none text-foreground">
                  {fp("sameAsInquirerAddress.label")}
                </span>
              </label>
            </div>
            <Field label={fp("elderDistrict.label")} required>
              <SearchableSelect
                value={elderLocation.value.districtId}
                onValueChange={setElderDistrict}
                onSearch={elderLocation.searchDistricts}
                seedOption={locationSeedOption(
                  elderLocation.value.districtId,
                  elderAddressLabels.districtName
                )}
                placeholder={fp("elderDistrict.placeholder")}
                searchPlaceholder={fp("elderDistrict.searchPlaceholder")}
                emptyText={fp("elderDistrict.emptyText")}
                disabled={sameAsInquirerAddress}
              />
            </Field>
            <Field label={fp("elderCity.label")} required>
              <SearchableSelect
                value={elderLocation.value.cityId}
                onValueChange={setElderCity}
                onSearch={elderLocation.searchCities}
                seedOption={locationSeedOption(
                  elderLocation.value.cityId,
                  elderAddressLabels.cityName
                )}
                placeholder={elderLocation.cityPlaceholder}
                searchPlaceholder={fp("elderCity.searchPlaceholder")}
                emptyText={
                  elderLocation.cityDisabled
                    ? fp("elderCity.emptyDisabled")
                    : fp("elderCity.empty")
                }
                disabled={sameAsInquirerAddress || elderLocation.cityDisabled}
              />
            </Field>
            <Field label={fp("elderArea.label")} required>
              <SearchableSelect
                value={elderLocation.value.areaId}
                onValueChange={setElderArea}
                onSearch={elderLocation.searchAreas}
                seedOption={locationSeedOption(
                  elderLocation.value.areaId,
                  elderAddressLabels.areaName
                )}
                placeholder={elderLocation.areaPlaceholder}
                searchPlaceholder={fp("elderArea.searchPlaceholder")}
                emptyText={
                  elderLocation.areaDisabled
                    ? fp("elderArea.emptyDisabled")
                    : fp("elderArea.empty")
                }
                disabled={sameAsInquirerAddress || elderLocation.areaDisabled}
              />
            </Field>
            <Field label={fp("elderPincode.label")} required>
              <Input
                value={form.elderPincode}
                onChange={e => {
                  setSameAsInquirerAddress(false);
                  setField(
                    "elderPincode",
                    e.target.value.replace(/[^0-9]/g, "")
                  );
                }}
                placeholder={fp("elderPincode.placeholder")}
                maxLength={6}
                inputMode="numeric"
                disabled={sameAsInquirerAddress}
              />
            </Field>
            <Field label={fp("elderLandmark.label")} full>
              <Input
                value={form.elderLandmark}
                onChange={e => {
                  setSameAsInquirerAddress(false);
                  setField("elderLandmark", e.target.value);
                }}
                placeholder={fp("elderLandmark.placeholder")}
                disabled={sameAsInquirerAddress}
              />
            </Field>
            <Field label={fp("physicalHealthStatus.label")}>
              <Textarea
                rows={2}
                value={form.physicalHealthStatus}
                onChange={e => setField("physicalHealthStatus", e.target.value)}
                placeholder={fp("physicalHealthStatus.placeholder")}
              />
            </Field>
            <Field label={fp("elderWeight.label")}>
              <Input
                type="number"
                inputMode="decimal"
                min={1}
                max={500}
                step="0.1"
                value={form.elderWeight}
                onChange={e => setField("elderWeight", e.target.value)}
                placeholder={fp("elderWeight.placeholder")}
              />
            </Field>
            <Field label={fp("reasonForAdmission.label")} full required>
              <Textarea
                rows={3}
                value={form.reasonForAdmission}
                onChange={e => setField("reasonForAdmission", e.target.value)}
                placeholder={fp("reasonForAdmission.placeholder")}
              />
            </Field>
          </StepPanel>
        )}

        {/* Step 4 — Income & Contacts */}
        {step === 3 && (
          <StepPanel step={current}>
            <Field label={fp("pensionAmount.label")}>
              <Input
                inputMode="decimal"
                value={form.pensionAmount}
                onChange={e =>
                  setField(
                    "pensionAmount",
                    e.target.value
                      .replace(/[^0-9.]/g, "")
                      .replace(/(\..*)\./g, "$1")
                  )
                }
                placeholder={fp("pensionAmount.placeholder")}
              />
            </Field>
            <Field label={fp("otherIncome.label")} required>
              <Input
                inputMode="decimal"
                value={form.otherIncome}
                onChange={e =>
                  setField(
                    "otherIncome",
                    e.target.value
                      .replace(/[^0-9.]/g, "")
                      .replace(/(\..*)\./g, "$1")
                  )
                }
                placeholder={fp("otherIncome.placeholder")}
              />
            </Field>
            <Field label={fp("otherFinancierName.label")} full>
              <Input
                value={form.otherFinancierName}
                onChange={e => setField("otherFinancierName", e.target.value)}
                placeholder={fp("otherFinancierName.placeholder")}
              />
            </Field>
            <Field label={fp("otherFinancierNumber.label")}>
              <Input
                type="tel"
                inputMode="numeric"
                value={form.otherFinancierNumber}
                onChange={e =>
                  setField(
                    "otherFinancierNumber",
                    e.target.value.replace(/[^0-9]/g, "").slice(0, 15)
                  )
                }
                placeholder={fp("otherFinancierNumber.placeholder")}
              />
            </Field>
            <Field label={fp("otherFinancierEmail.label")}>
              <Input
                type="email"
                value={form.otherFinancierEmail}
                onChange={e => setField("otherFinancierEmail", e.target.value)}
                placeholder={fp("otherFinancierEmail.placeholder")}
              />
            </Field>
          </StepPanel>
        )}

        {/* Step 5 — Health Conditions */}
        {step === 4 && (
          <StepPanel step={current}>
            <ToggleField
              label={fp("hasPhysicalDisability.label")}
              checked={form.hasPhysicalDisability}
              onCheckedChange={v => setField("hasPhysicalDisability", v)}
            />
            {form.hasPhysicalDisability && (
              <Field label={fp("physicalDisabilityDetails.label")} full>
                <Textarea
                  rows={2}
                  value={form.physicalDisabilityDetails}
                  onChange={e =>
                    setField("physicalDisabilityDetails", e.target.value)
                  }
                  placeholder={fp("physicalDisabilityDetails.placeholder")}
                />
              </Field>
            )}

            <ToggleField
              label={fp("hasMentalHealthIssue.label")}
              checked={form.hasMentalHealthIssue}
              onCheckedChange={v => setField("hasMentalHealthIssue", v)}
            />
            {form.hasMentalHealthIssue && (
              <Field label={fp("mentalHealthIssueDetails.label")} full>
                <Textarea
                  rows={2}
                  value={form.mentalHealthIssueDetails}
                  onChange={e =>
                    setField("mentalHealthIssueDetails", e.target.value)
                  }
                  placeholder={fp("mentalHealthIssueDetails.placeholder")}
                />
              </Field>
            )}

            <ToggleField
              label={fp("hasExistingDisease.label")}
              checked={form.hasExistingDisease}
              onCheckedChange={v => setField("hasExistingDisease", v)}
            />
            {form.hasExistingDisease && (
              <Field label={fp("diseaseDetails.label")} full>
                <Textarea
                  rows={2}
                  value={form.diseaseDetails}
                  onChange={e => setField("diseaseDetails", e.target.value)}
                  placeholder={fp("diseaseDetails.placeholder")}
                />
              </Field>
            )}

            <ToggleField
              label={fp("hasFoodAllergy.label")}
              checked={form.hasFoodAllergy}
              onCheckedChange={v => setField("hasFoodAllergy", v)}
            />
            {form.hasFoodAllergy && (
              <Field label={fp("foodAllergyDetails.label")} full>
                <Textarea
                  rows={2}
                  value={form.foodAllergyDetails}
                  onChange={e => setField("foodAllergyDetails", e.target.value)}
                  placeholder={fp("foodAllergyDetails.placeholder")}
                />
              </Field>
            )}

            <ToggleField
              label={fp("hasMedicineAllergy.label")}
              checked={form.hasMedicineAllergy}
              onCheckedChange={v => setField("hasMedicineAllergy", v)}
            />
            {form.hasMedicineAllergy && (
              <Field label={fp("medicineAllergyDetails.label")} full>
                <Textarea
                  rows={2}
                  value={form.medicineAllergyDetails}
                  onChange={e =>
                    setField("medicineAllergyDetails", e.target.value)
                  }
                  placeholder={fp("medicineAllergyDetails.placeholder")}
                />
              </Field>
            )}

            <Field label={fp("specialMedicalInstructions.label")} full>
              <Textarea
                rows={3}
                value={form.specialMedicalInstructions}
                onChange={e =>
                  setField("specialMedicalInstructions", e.target.value)
                }
                placeholder={fp("specialMedicalInstructions.placeholder")}
              />
            </Field>
          </StepPanel>
        )}

        {/* Step 6 — Current Treatment */}
        {step === 5 && (
          <StepPanel step={current}>
            <ToggleField
              label={fp("currentlyUnderTreatment.label")}
              checked={form.currentlyUnderTreatment}
              onCheckedChange={v => {
                setForm(prev => ({
                  ...prev,
                  currentlyUnderTreatment: v,
                  ...(v
                    ? {}
                    : {
                        currentDoctorName: "",
                        currentHospitalName: "",
                        currentHospitalAddress: "",
                      }),
                }));
              }}
            />
            {form.currentlyUnderTreatment && (
              <>
                <Field label={fp("currentDoctorName.label")}>
                  <Input
                    value={form.currentDoctorName}
                    onChange={e =>
                      setField("currentDoctorName", e.target.value)
                    }
                    placeholder={fp("currentDoctorName.placeholder")}
                  />
                </Field>
                <Field label={fp("currentHospitalName.label")}>
                  <Input
                    value={form.currentHospitalName}
                    onChange={e =>
                      setField("currentHospitalName", e.target.value)
                    }
                    placeholder={fp("currentHospitalName.placeholder")}
                  />
                </Field>
                <Field label={fp("currentHospitalAddress.label")} full>
                  <Input
                    value={form.currentHospitalAddress}
                    onChange={e =>
                      setField("currentHospitalAddress", e.target.value)
                    }
                    placeholder={fp("currentHospitalAddress.placeholder")}
                  />
                </Field>
              </>
            )}

            <ToggleField
              label={fp("takesCurrentMedicines.label")}
              checked={form.takesCurrentMedicines}
              onCheckedChange={v => setField("takesCurrentMedicines", v)}
            />
            {form.takesCurrentMedicines && (
              <div className="md:col-span-2">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <p className="text-foreground/80 text-xs font-medium">
                    {fp("medicines.label")}
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-xl"
                    onClick={() =>
                      setMedicines(rows => [...rows, createMedicineRow()])
                    }
                  >
                    <Plus className="mr-1 h-3.5 w-3.5" />{" "}
                    {fp("medicines.addMedicine")}
                  </Button>
                </div>

                <div className="space-y-3">
                  {medicines.map((row, i) => (
                    <div
                      key={row.rowId}
                      className="rounded-xl border border-border bg-background p-4"
                    >
                      <div className="mb-3 flex items-center justify-between border-b border-border pb-2">
                        <span className="text-xs font-semibold text-muted-foreground">
                          {fp("medicines.medicine")} {i + 1}
                        </span>
                        {medicines.length > 1 && (
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-destructive"
                            onClick={() =>
                              setMedicines(rows =>
                                rows.filter(r => r.rowId !== row.rowId)
                              )
                            }
                            aria-label={`${fp("medicines.remove")} ${i + 1}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <div className="grid gap-3 md:grid-cols-2">
                        <Field label={fp("medicines.treatmentName")} full>
                          {row.treatmentNameCustom ? (
                            <div className="space-y-1.5">
                              <Input
                                value={row.currentTreatmentName}
                                onChange={e =>
                                  updateMedicine(row.rowId, {
                                    currentTreatmentName: e.target.value,
                                  })
                                }
                                placeholder={fp(
                                  "medicines.treatmentNamePlaceholder"
                                )}
                              />
                              <button
                                type="button"
                                className="text-xs font-medium text-primary hover:underline"
                                onClick={() =>
                                  updateMedicine(row.rowId, {
                                    treatmentNameCustom: false,
                                    currentTreatmentName: "",
                                  })
                                }
                              >
                                {fp("medicines.treatmentBackToList")}
                              </button>
                            </div>
                          ) : (
                            <SearchableSelect
                              value={row.currentTreatmentName}
                              onValueChange={value => {
                                if (value === TREATMENT_OTHER_VALUE) {
                                  updateMedicine(row.rowId, {
                                    treatmentNameCustom: true,
                                    currentTreatmentName: "",
                                  });
                                  return;
                                }
                                updateMedicine(row.rowId, {
                                  currentTreatmentName: value,
                                });
                              }}
                              onSearch={searchTreatments}
                              seedOption={
                                row.currentTreatmentName
                                  ? {
                                      value: row.currentTreatmentName,
                                      label: row.currentTreatmentName,
                                    }
                                  : undefined
                              }
                              placeholder={fp(
                                "medicines.treatmentNamePlaceholder"
                              )}
                              searchPlaceholder={fp(
                                "medicines.treatmentSearchPlaceholder"
                              )}
                              emptyText={fp("medicines.treatmentEmpty")}
                            />
                          )}
                        </Field>
                        <Field label={fp("medicines.name")}>
                          <Input
                            value={row.name}
                            onChange={e =>
                              updateMedicine(row.rowId, {
                                name: e.target.value,
                              })
                            }
                            placeholder={fp("medicines.namePlaceholder")}
                          />
                        </Field>
                        <Field label={fp("medicines.dosage")}>
                          <Input
                            value={row.dosage}
                            onChange={e =>
                              updateMedicine(row.rowId, {
                                dosage: e.target.value,
                              })
                            }
                            placeholder={fp("medicines.dosagePlaceholder")}
                          />
                        </Field>
                        <Field label={fp("medicines.frequency")} full>
                          <Input
                            value={row.frequency}
                            onChange={e =>
                              updateMedicine(row.rowId, {
                                frequency: e.target.value,
                              })
                            }
                            placeholder={fp("medicines.frequencyPlaceholder")}
                          />
                        </Field>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </StepPanel>
        )}

        {/* Step 7 — Operation History */}
        {step === 6 && (
          <StepPanel step={current}>
            <ToggleField
              label={fp("hadOperationBefore.label")}
              checked={form.hadOperationBefore}
              onCheckedChange={v => setField("hadOperationBefore", v)}
            />
            {form.hadOperationBefore && (
              <div className="md:col-span-2">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <p className="text-foreground/80 text-xs font-medium">
                    {fp("operations.label")}
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-xl"
                    onClick={() =>
                      setOperations(rows => [...rows, createOperationRow()])
                    }
                  >
                    <Plus className="mr-1 h-3.5 w-3.5" />{" "}
                    {fp("operations.addOperation")}
                  </Button>
                </div>

                <div className="space-y-3">
                  {operations.map((row, i) => (
                    <div
                      key={row.rowId}
                      className="rounded-xl border border-border bg-background p-4"
                    >
                      <div className="mb-3 flex items-center justify-between border-b border-border pb-2">
                        <span className="text-xs font-semibold text-muted-foreground">
                          {fp("operations.operation")} {i + 1}
                        </span>
                        {operations.length > 1 && (
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-destructive"
                            onClick={() =>
                              setOperations(rows =>
                                rows.filter(r => r.rowId !== row.rowId)
                              )
                            }
                            aria-label={`${fp("operations.remove")} ${i + 1}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <div className="grid gap-3 md:grid-cols-2">
                        <Field label={fp("operations.type")}>
                          <Input
                            value={row.operationType}
                            onChange={e =>
                              updateOperation(row.rowId, {
                                operationType: e.target.value,
                              })
                            }
                            placeholder={fp("operations.typePlaceholder")}
                          />
                        </Field>
                        <Field label={fp("operations.date")}>
                          <Input
                            type="date"
                            value={row.operationDate}
                            onChange={e =>
                              updateOperation(row.rowId, {
                                operationDate: e.target.value,
                              })
                            }
                          />
                        </Field>
                        <Field label={fp("operations.doctorName")}>
                          <Input
                            value={row.operationDoctorName}
                            onChange={e =>
                              updateOperation(row.rowId, {
                                operationDoctorName: e.target.value,
                              })
                            }
                            placeholder={fp("operations.doctorNamePlaceholder")}
                          />
                        </Field>
                        <Field label={fp("operations.hospitalName")}>
                          <Input
                            value={row.operationHospitalName}
                            onChange={e =>
                              updateOperation(row.rowId, {
                                operationHospitalName: e.target.value,
                              })
                            }
                            placeholder={fp(
                              "operations.hospitalNamePlaceholder"
                            )}
                          />
                        </Field>
                        <Field label={fp("operations.district.label")}>
                          <SearchableSelect
                            value={row.districtId}
                            onValueChange={(value, option) =>
                              updateOperation(row.rowId, {
                                districtId: value,
                                cityId: "",
                                areaId: "",
                                districtName: option?.label ?? "",
                                cityName: "",
                                areaName: "",
                              })
                            }
                            onSearch={searchDistrictOptions}
                            seedOption={locationSeedOption(
                              row.districtId,
                              row.districtName
                            )}
                            placeholder={fp("operations.district.placeholder")}
                            searchPlaceholder={fp(
                              "operations.district.searchPlaceholder"
                            )}
                            emptyText={fp("operations.district.emptyText")}
                          />
                        </Field>
                        <Field label={fp("operations.city.label")}>
                          <SearchableSelect
                            value={row.cityId}
                            onValueChange={(value, option) =>
                              updateOperation(row.rowId, {
                                cityId: value,
                                areaId: "",
                                cityName: option?.label ?? "",
                                areaName: "",
                              })
                            }
                            onSearch={query =>
                              searchCityOptions(row.districtId, query)
                            }
                            seedOption={locationSeedOption(
                              row.cityId,
                              row.cityName
                            )}
                            placeholder={
                              row.districtId
                                ? fp("operations.city.placeholder")
                                : fp("operations.city.emptyDisabled")
                            }
                            searchPlaceholder={fp(
                              "operations.city.searchPlaceholder"
                            )}
                            emptyText={
                              row.districtId
                                ? fp("operations.city.empty")
                                : fp("operations.city.emptyDisabled")
                            }
                            disabled={!row.districtId}
                          />
                        </Field>
                        <Field label={fp("operations.area.label")} full>
                          <SearchableSelect
                            value={row.areaId}
                            onValueChange={(value, option) =>
                              updateOperation(row.rowId, {
                                areaId: value,
                                areaName: option?.label ?? "",
                              })
                            }
                            onSearch={query =>
                              searchAreaOptions(row.cityId, query)
                            }
                            seedOption={locationSeedOption(
                              row.areaId,
                              row.areaName
                            )}
                            placeholder={
                              row.cityId
                                ? fp("operations.area.placeholder")
                                : fp("operations.area.emptyDisabled")
                            }
                            searchPlaceholder={fp(
                              "operations.area.searchPlaceholder"
                            )}
                            emptyText={
                              row.cityId
                                ? fp("operations.area.empty")
                                : fp("operations.area.emptyDisabled")
                            }
                            disabled={!row.cityId}
                          />
                        </Field>
                        <Field label={fp("operations.details")} full>
                          <Textarea
                            rows={2}
                            value={row.operationDetails}
                            onChange={e =>
                              updateOperation(row.rowId, {
                                operationDetails: e.target.value,
                              })
                            }
                            placeholder={fp("operations.detailsPlaceholder")}
                          />
                        </Field>
                        <div className="md:col-span-2">
                          <ToggleField
                            label={fp("operations.takesRecoveryMedicine")}
                            checked={row.takesRecoveryMedicine}
                            onCheckedChange={v =>
                              updateOperation(row.rowId, {
                                takesRecoveryMedicine: v,
                              })
                            }
                            className="bg-card"
                          />
                        </div>
                        {row.takesRecoveryMedicine && (
                          <Field
                            label={fp("operations.recoveryMedicineDetails")}
                            full
                          >
                            <Textarea
                              rows={2}
                              value={row.recoveryMedicineDetails}
                              onChange={e =>
                                updateOperation(row.rowId, {
                                  recoveryMedicineDetails: e.target.value,
                                })
                              }
                              placeholder={fp(
                                "operations.recoveryMedicineDetailsPlaceholder"
                              )}
                            />
                          </Field>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </StepPanel>
        )}

        {/* Step 8 — Additional Notes */}
        {step === 7 && (
          <StepPanel step={current}>
            <Field label={fp("additionalNotes.label")} full>
              <Textarea
                rows={6}
                value={form.additionalNotes}
                onChange={e => setField("additionalNotes", e.target.value)}
                placeholder={fp("additionalNotes.placeholder")}
              />
            </Field>
            {!isAdmin && (
              <div className="border-primary/30 bg-primary/5 rounded-xl border border-dashed p-4 md:col-span-2">
                <p className="text-xs leading-relaxed text-muted-foreground">
                  {t("account.inquiryForm.consent")}
                </p>
              </div>
            )}
          </StepPanel>
        )}

        <div className="flex flex-col-reverse items-stretch justify-between gap-3 sm:flex-row sm:items-center">
          <Button
            type="button"
            variant="outline"
            onClick={goBack}
            disabled={isFirst}
            className="gap-1 rounded-xl"
          >
            <ChevronLeft className="h-4 w-4" />{" "}
            {t("account.inquiryForm.nav.previous")}
          </Button>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            {!isLast && (
              <p className="text-center text-xs text-muted-foreground sm:text-right">
                {steps.length - step - 1 === 1
                  ? t("account.inquiryForm.nav.remainingOne", {
                      count: steps.length - step - 1,
                    })
                  : t("account.inquiryForm.nav.remainingOther", {
                      count: steps.length - step - 1,
                    })}
              </p>
            )}
            {isLast ? (
              <Button
                type="submit"
                disabled={submitting}
                className={cn(
                  "gap-1 rounded-xl",
                  isAdmin ? "" : "hover:bg-gold/90 bg-gold text-gold-foreground"
                )}
              >
                {isAdmin ? (
                  <Save className="h-4 w-4" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                {submitting
                  ? t("account.inquiryForm.submit.saving")
                  : isAdmin
                    ? isEditing
                      ? t("account.inquiryForm.submit.update")
                      : t("account.inquiryForm.submit.save")
                    : t("account.inquiryForm.submit.submit")}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={e => {
                  e.preventDefault();
                  goNext();
                }}
                className="gap-1 rounded-xl"
              >
                {t("account.inquiryForm.nav.next")}{" "}
                <ChevronRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
